import { useState, useEffect } from 'react';
import { db, storage } from '../../../firebase.config';
import { collection, addDoc, updateDoc, doc, deleteDoc, getDocs } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject, uploadBytes } from 'firebase/storage'; 
import '../../../styles/manageProducts.css';

const ManageProducts = () => {
    const [products, setProducts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [productData, setProductData] = useState({
        category: '',
        productCategory: '',
        productSize: '',
        details: '',
        imgUrl: [],
        price: '',
        weight: '',
        productName: '',
        productType: '',
        returns: '',
        shipping: '',
        isPremium: false,
        variations: [
            {
                color: '',
                hex: '',
                imgUrl: [],
                price: 0,
                variationName: '',
                sizes: [
                    { size: '', quantity: 0, price: 0 }
                ]
            },
        ]
    });
    const [editProductId, setEditProductId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedImages, setSelectedImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
        index: null,
    });

    useEffect(() => {
        const handleClickOutside = () => {
          if (contextMenu.visible) setContextMenu({ ...contextMenu, visible: false });
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [contextMenu]);
    
    // Fetch all products
    const fetchProducts = async () => {
        const productsRef = collection(db, "products");
        const querySnapshot = await getDocs(productsRef);
        
        const products = querySnapshot.docs.map(doc => {
            const data = doc.data();

            return {
                id: doc.id,
                ...data,
                variations: Array.isArray(data.variations) ? data.variations : [], // ensure it's always an array
            };
        });

        console.log("Fetched products:", products);
        setProducts(products);
        setLoading(false);
    };

    // Add product
    const addProduct = async () => {
        try {
            setLoading(true);
    
            // Upload main product images
            const uploadedImageUrls = [];
            for (const file of selectedImages) {
                const storageRef = ref(storage, `products/${productData.category}/${productData.productName}/main/${file.name}`);
                const uploadTask = uploadBytesResumable(storageRef, file);
                await new Promise((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        null,
                        (error) => reject(error),
                        async () => {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            uploadedImageUrls.push(downloadURL);
                            resolve();
                        }
                    );
                });
            }
    
            // Upload each variation's image (if it's a file, not already a URL)
            const uploadedVariations = await Promise.all(
                productData.variations.map(async (variation, index) => {
                    try {
                        if (variation.imgUrl && typeof variation.imgUrl !== "string" && variation.imgUrl.name)  {
                            console.log(`Uploading variation ${index}:`, variation.imgUrl.name);
                            const fileName = variation.imgUrl.name || `variation-${index}.jpg`;
                            const storageRef = ref(storage, `products/${productData.category}/${productData.productName}/variations/${fileName}`);
                            const uploadTask = uploadBytesResumable(storageRef, variation.imgUrl);
            
                            const downloadURL = await new Promise((resolve, reject) => {
                                uploadTask.on(
                                    "state_changed",
                                    null,
                                    (error) => {
                                        console.error("Upload error:", error);
                                        reject(error);
                                    },
                                    async () => {
                                        const url = await getDownloadURL(uploadTask.snapshot.ref);
                                        console.log(`Uploaded variation ${index} to:`, url);
                                        resolve(url);
                                    }
                                );
                            });
                            console.log(`Variation ${index}:`, variation.imgUrl);
            
                            return { ...variation, imgUrl: downloadURL };
                        } else {
                            return variation; // already a URL
                        }
                    } catch (err) {
                        console.error(`Variation image upload failed for index ${index}:`, err);
                        return variation;
                    }
                })
            );
    
            // Prepare final product object
            const newProductData = {
                ...productData,
                quantity: calculateTotalQuantityFromColors(),
                imgUrl: uploadedImageUrls, // main product images
                variations: uploadedVariations, // now include variation image URLs
            };
    
            const productsRef = collection(db, "products");
            await addDoc(productsRef, newProductData);
    
            setLoading(false);
            setShowModal(false);
            setSelectedImages([]); // Clear selected images
            fetchProducts();
        } catch (error) {
            console.error("Error adding product:", error);
            setLoading(false);
        }
    };
    
    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
    
        const uploadPromises = files.map(file => {
            const storageRef = ref(storage, `product-images/${file.name}-${Date.now()}`);
            return uploadBytes(storageRef, file).then(snapshot => getDownloadURL(snapshot.ref));
        });
    
        const urls = await Promise.all(uploadPromises);
    
        setProductData(prev => {
            const existing = Array.isArray(prev.imgUrl) ? prev.imgUrl : [];
            return {
                ...prev,
                imgUrl: [...existing, ...urls],
            };
        });
    };
    
    const setImageAsMain = (index) => {
        setProductData(prev => {
            console.log("Previous productData in setImageAsMain:", prev);
    
            if (!prev || !Array.isArray(prev.imgUrl)) return prev;
    
            const images = [...prev.imgUrl];
            if (!images[index]) return prev;
    
            const [selected] = images.splice(index, 1);
            images.unshift(selected);
    
            return { ...prev, imgUrl: images };
        });
    };

    const handleVariationImageUpload = async (e, vIndex) => {
        const files = Array.from(e.target.files);
    
        const uploadPromises = files.map(file => {
            const storageRef = ref(storage, `variation-images/v${vIndex}/${file.name}-${Date.now()}`);
            return uploadBytes(storageRef, file).then(snapshot => getDownloadURL(snapshot.ref));
        });
    
        const urls = await Promise.all(uploadPromises);
    
        setProductData(prev => {
            const updated = [...prev.variations];
            const existing = Array.isArray(updated[vIndex].imgUrl) ? updated[vIndex].imgUrl : [];
            updated[vIndex].imgUrl = [...existing, ...urls];
            return { ...prev, variations: updated };
        });
    };

    const makeVariationImageMain = (vIndex, imgIndex) => {
        setProductData(prev => {
            const updated = [...prev.variations];
            const imgList = updated[vIndex].imgUrl;
            const [selected] = imgList.splice(imgIndex, 1);
            updated[vIndex].imgUrl = [selected, ...imgList];
            return { ...prev, variations: updated };
        });
    };
    

    const calculateTotalQuantityFromColors = () => {
        return (productData.colors || []).reduce((total, color) => {
            return total + (color.sizes || []).reduce((sum, sizeObj) => sum + (parseInt(sizeObj.qty) || 0), 0);
        }, 0);
    };

    // Edit product
    const editProduct = async () => {
        try {
            setLoading(true);
            const productRef = doc(db, "products", editProductId);
    
            // 1. Upload new main product images if selected
            const uploadedImageUrls = [];
            for (const file of selectedImages || []) {
                const storageRef = ref(storage, `products/${productData.category}/${productData.productName}/main/${file.name}`);
                const uploadTask = uploadBytesResumable(storageRef, file);
                await new Promise((resolve, reject) => {
                    uploadTask.on(
                        "state_changed",
                        null,
                        (error) => reject(error),
                        async () => {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            uploadedImageUrls.push(downloadURL);
                            resolve();
                        }
                    );
                });
            }
    
            // 2. Upload variation images (if they are files, not URLs)
            const uploadedVariations = await Promise.all(
                productData.variations.map(async (variation, index) => {
                    if (variation.imgUrl && typeof variation.imgUrl !== "string" && variation.imgUrl.name) {
                        const fileName = variation.imgUrl.name || `variation-${index}.jpg`;
                        const storageRef = ref(storage, `products/${productData.category}/${productData.productName}/variations/${fileName}`);
                        const uploadTask = uploadBytesResumable(storageRef, variation.imgUrl);
                        const downloadURL = await new Promise((resolve, reject) => {
                            uploadTask.on(
                                "state_changed",
                                null,
                                (error) => reject(error),
                                async () => {
                                    const url = await getDownloadURL(uploadTask.snapshot.ref);
                                    resolve(url);
                                }
                            );
                        });
                        return { ...variation, imgUrl: downloadURL };
                    }
                    return variation;
                })
            );
    
            // 3. Build updated product object
            const updatedData = {
                ...productData,
                quantity: calculateTotalQuantityFromColors(),
                imgUrl: uploadedImageUrls.length > 0 ? uploadedImageUrls : productData.imgUrl, // keep existing if none uploaded
                variations: uploadedVariations
            };
    
            // 4. Update Firestore document
            await updateDoc(productRef, updatedData);
    
            setLoading(false);
            setShowModal(false);
            setSelectedImages([]);
            fetchProducts();
        } catch (error) {
            console.error("Error editing product:", error);
            setLoading(false);
        }
    };

    const handleMainImageDelete = async (index) => {
        const imageToDelete = productData.imgUrl[index];
    
        if (window.confirm("Are you sure you want to delete this image?")) {
            try {
                // If it's a URL (already uploaded)
                if (typeof imageToDelete === 'string' && imageToDelete.startsWith('https://')) {
                    const path = imageToDelete.split('/o/')[1].split('?')[0]; // encoded path
                    const decodedPath = decodeURIComponent(path);
                    const imageRef = ref(storage, decodedPath);
                    await deleteObject(imageRef);
                }
    
                const updatedImages = productData.imgUrl.filter((_, i) => i !== index);
                setProductData(prev => ({ ...prev, imgUrl: updatedImages }));
            } catch (err) {
                console.error("Error deleting image:", err);
                alert("Failed to delete image.");
            }
        }
    };

    const handleVariationImageDelete = (vIndex, imgIndex) => {
        if (window.confirm("Are you sure you want to delete this image?")) {
            setProductData(prev => {
                const updated = [...prev.variations];
                const imgs = Array.isArray(updated[vIndex].imgUrl) ? [...updated[vIndex].imgUrl] : [];
                imgs.splice(imgIndex, 1);
                updated[vIndex].imgUrl = imgs;
                return { ...prev, variations: updated };
            });
        }
    };
    
    // Delete product
    const deleteProduct = async (id) => {
        const confirmation = window.confirm("Are you sure you want to delete this product?");
        if (confirmation) {
            try {
                const productRef = doc(db, "products", id);
                await deleteDoc(productRef);
                fetchProducts();
            } catch (error) {
                console.error("Error deleting product:", error);
            }
        }
    };

    // Handle modal open for adding or editing
    const openModal = (product = null) => {
        if (product) {
            setProductData(product); // Pre-fill form with product data for editing
            setEditProductId(product.id);
        } else {
            setProductData({
                category: '',
                productCategory: '',
                details: '',
                imgUrl: [],
                price: '',
                productName: '',
                productType: '',
                returns: '',
                shipping: '',
                sizes: {},
            });
            setEditProductId(null);
        }
        setShowModal(true);
    };

    const updateVariation = (index, key, value) => {
        const updated = [...productData.variations];
        updated[index][key] = value;
        setProductData({ ...productData, variations: updated });
    };
      
    const updateSize = (vIndex, sIndex, key, value) => {
        const updated = [...productData.variations];
        updated[vIndex].sizes[sIndex][key] = value;
        setProductData({ ...productData, variations: updated });
    };

    const addSizeToVariation = (vIndex) => {
        const updated = [...productData.variations];
        updated[vIndex].sizes.push({ size: "", quantity: 0, price: 0 });
        setProductData({ ...productData, variations: updated });
    };

    const addNewVariation = () => {
        const newVariant = {
            color: "",
            hex: "",
            imgUrl: "",
            price: 0,
            variationName: "",
            sizes: [],
        };
        setProductData(prev => ({
            ...prev,
            variations: Array.isArray(prev.variations) ? [...prev.variations, newVariant] : [newVariant],
        }));
    };

    // const uploadToStorage = async (file, path) => {
    //     const storage = getStorage();
    //     const storageRef = ref(storage, `${path}/${file.name}`);
    //     const uploadTask = uploadBytesResumable(storageRef, file);

    //     return new Promise((resolve, reject) => {
    //         uploadTask.on(
    //             "state_changed",
    //             null,
    //             (error) => {
    //                 console.error("Upload failed:", error);
    //                 reject(error);
    //             },
    //             async () => {
    //                 const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
    //                 resolve(downloadURL);
    //             }
    //         );
    //     });
    // };


    // Fetch products when component mounts or when products change
    useEffect(() => {
        fetchProducts();
    }, []);

    if (loading) return <p>Loading products...</p>;

    return (
        <div className="manage-products">
            <h1>Manage Products</h1>
            <button onClick={() => openModal()}>Add Product</button>

            {/* Products Table */}
            <table className="products-table">
                <thead>
                    <tr>
                        <th>Product Name</th>
                        <th>Category</th>
                        <th>Product Category</th>
                        <th>Product Size</th>
                        <th>Base Price</th>
                        <th>Weight</th>
                        <th>Premium</th>
                        <th>No. of Variaitons</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {products.map((product) => (
                        <tr key={product.id}>
                            <td>{product.productName}</td>
                            <td>{product.category}</td>
                            <td>{product.productCategory}</td>
                            <td>{product.productSize}</td>
                            <td>${product.price}</td>
                            <td>{product.weight}</td>
                            <td>
                                {product.isPremium ? (
                                    <i className="ri-check-line text-success"></i>
                                ) : (
                                    <i className="ri-close-line text-danger"></i>
                                )}
                            </td>
                            <td>{product?.variations.length}</td>
                            <td>
                                <button onClick={() => openModal(product)}>Update</button>
                                <button onClick={() => deleteProduct(product.id)}>Delete</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Modal for Add/Edit */}
            {showModal && (
                <div className="modal">
                    <div className="modal-cont">
                        <h2>{editProductId ? 'Edit Product' : 'Add Product'}</h2>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            if (editProductId) {
                                editProduct();
                            } else {
                                addProduct();
                            }
                        }}>
                            <div className="form-group">
                                <label>Product Name</label>
                                <input
                                    type="text"
                                    value={productData.productName}
                                    onChange={(e) => setProductData({ ...productData, productName: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Category</label>
                                <select
                                    value={productData.category}
                                    onChange={(e) => setProductData({ ...productData, category: e.target.value })}
                                    required
                                >
                                    <option value="">Select Category</option>
                                    <option value="Physical">Physical</option>
                                    <option value="NFT">NFT</option>
                                    <option value="Grotto">Grotto</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Product Category</label>
                                <select
                                    value={productData.productCategory}
                                    onChange={(e) => setProductData({ ...productData, productCategory: e.target.value })}
                                    required
                                >
                                    <option value="">Select Product Category</option>
                                    <option value="tshirts">TShirt</option>
                                    <option value="hoodies">Hoodies</option>
                                    <option value="pants">Pant</option>
                                    <option value="rugs">Rug</option>
                                    <option value="accessories">Accessories</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Product Size</label>
                                <select
                                    value={productData.productSize}
                                    onChange={(e) => setProductData({ ...productData, productSize: e.target.value })}
                                    required
                                >
                                    <option value="">Select Size</option>
                                    <option value="small">Small</option>
                                    <option value="large">Large</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Details</label>
                                <input
                                    type="text"
                                    value={productData.details}
                                    onChange={(e) => setProductData({ ...productData, details: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Price</label>
                                <input
                                    type="number"
                                    value={productData.price}
                                    onChange={(e) => setProductData({ ...productData, price: parseFloat(e.target.value) })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Weight in ounce</label>
                                <input
                                    type="number"
                                    value={productData.weight}
                                    onChange={(e) => setProductData({ ...productData, weight: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Return Policy</label>
                                <textarea
                                    value={productData.returns}
                                    onChange={(e) => setProductData({ ...productData, returns: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Shipping</label>
                                <input
                                    type="text"
                                    value={productData.shipping}
                                    onChange={(e) => setProductData({ ...productData, shipping: e.target.value })}
                                />
                            </div>
                            <div className="form-group d-flex align-items-center" style={{ gap: '6px' }}>
                                <label className="mb-0" htmlFor="isPremium">Premium?</label>
                                <input
                                    type="checkbox"
                                    id="isPremium"
                                    style={{ margin: 0, width: '30px', cursor: 'pointer' }}
                                    checked={productData.isPremium}
                                    onChange={(e) =>
                                        setProductData({ ...productData, isPremium: e.target.checked })
                                    }
                                />
                            </div>

                            <div className="form-group">
                                <label>Product Images</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleImageUpload}
                                />
                                {uploading && <p>Uploading images...</p>}

                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' }}>
                                    {Array.isArray(productData.imgUrl) && productData.imgUrl.map((img, index) => (
                                        <img
                                            key={index}
                                            src={img instanceof File ? URL.createObjectURL(img) : img}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                setContextMenu({
                                                    visible: true,
                                                    x: e.pageX,
                                                    y: e.pageY,
                                                    index,
                                                    type: 'main'
                                                });
                                            }}
                                            alt={`Product-${index}`}
                                            loading="lazy"
                                            style={{ width: '80px', cursor: 'pointer' }}
                                            onClick={() => handleMainImageDelete(index)}
                                            title="Click to delete"
                                        />
                                    ))}
                                </div>
                            </div>
                            {contextMenu.visible && (
                                <div
                                    className="context-menu"
                                    style={{ top: contextMenu.y, left: contextMenu.x }}
                                    onMouseLeave={() => setContextMenu({ ...contextMenu, visible: false })}
                                >
                                    <div
                                        className="context-menu-option"
                                        onClick={() => {
                                            if (contextMenu.type === 'main') {
                                                setImageAsMain(contextMenu.index);
                                            } else if (contextMenu.type === 'variation') {
                                                makeVariationImageMain(contextMenu.vIndex, contextMenu.imgIndex);
                                            }
                                            setContextMenu({ ...contextMenu, visible: false });
                                        }}
                                    >
                                        Set as display image
                                    </div>
                                    <div
                                        className="context-menu-option"
                                        onClick={() => {
                                            if (contextMenu.type === 'main') {
                                                handleMainImageDelete(contextMenu.index);
                                            } else if (contextMenu.type === 'variation') {
                                                handleVariationImageDelete(contextMenu.vIndex, contextMenu.imgIndex);
                                            }
                                            setContextMenu({ ...contextMenu, visible: false });
                                        }}
                                    >
                                        Delete
                                    </div>
                                </div>
)}



                            <button type="button" onClick={addNewVariation} className='btn-add'>+ Add Variation</button>

                            {Array.isArray(productData.variations) && productData.variations.length === 0 && (
                                <p>No variations yet. Click "Add Variation" to begin.</p>
                            )}
                            {Array.isArray(productData.variations) && productData.variations.length > 0 &&
                                productData.variations?.map((variant, vIndex) => {
                                return(
                                    <div key={vIndex} style={{ border: "1px solid #ccc", marginBottom: "1rem", padding: "1rem" }}>                                        
                                        <label>Color Name</label>
                                        <input
                                            type="text"
                                            value={variant.color}
                                            onChange={(e) => updateVariation(vIndex, "color", e.target.value)}
                                        />

                                        <label>Hex</label>
                                        <input
                                            type="text"
                                            value={variant.hex}
                                            onChange={(e) => updateVariation(vIndex, "hex", e.target.value)}
                                        />

                                        <label>Variation Name</label>
                                        <input
                                            type="text"
                                            value={variant.variationName}
                                            onChange={(e) => updateVariation(vIndex, "variationName", e.target.value)}
                                        />

                                        <label>Variation Image(s)</label>
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            multiple
                                            onChange={(e) => handleVariationImageUpload(e, vIndex)} 
                                        />
                                        {Array.isArray(variant.imgUrl) && variant.imgUrl.map((img, imgIndex) => (
                                            <img
                                                key={imgIndex}
                                                src={img instanceof File ? URL.createObjectURL(img) : img}
                                                alt={`Variant-${imgIndex}`}
                                                style={{
                                                    width: "80px",
                                                    marginRight: "5px",
                                                    cursor: "pointer",
                                                    border: imgIndex === 0 ? "2px solid green" : "1px solid #ccc"
                                                }}
                                                onContextMenu={(e) => {
                                                    e.preventDefault();
                                                    setContextMenu({
                                                        x: e.pageX,
                                                        y: e.pageY,
                                                        visible: true,
                                                        vIndex,
                                                        imgIndex,
                                                        type: 'variation'
                                                    });
                                                }}
                                            />
                                        ))}

                                        {contextMenu.visible && (
                                            <div
                                                className="context-menu"
                                                style={{ top: contextMenu.y, left: contextMenu.x }}
                                                onMouseLeave={() => setContextMenu({ ...contextMenu, visible: false })}
                                            >
                                                <div
                                                    className="context-menu-option"
                                                    onClick={() => {
                                                        if (contextMenu.type === 'main') {
                                                            setImageAsMain(contextMenu.index);
                                                        } else if (contextMenu.type === 'variation') {
                                                            makeVariationImageMain(contextMenu.vIndex, contextMenu.imgIndex);
                                                        }
                                                        setContextMenu({ ...contextMenu, visible: false });
                                                    }}
                                                >
                                                    Set as display image
                                                </div>
                                                <div
                                                    className="context-menu-option"
                                                    onClick={() => {
                                                        if (contextMenu.type === 'main') {
                                                            handleMainImageDelete(contextMenu.index);
                                                        } else if (contextMenu.type === 'variation') {
                                                            handleVariationImageDelete(contextMenu.vIndex, contextMenu.imgIndex);
                                                        }
                                                        setContextMenu({ ...contextMenu, visible: false });
                                                    }}
                                                >
                                                    Delete
                                                </div>
                                            </div>
                                            )}

                                        <label>Price</label>
                                        <input
                                            type="number"
                                            value={variant.price}
                                            onChange={(e) => updateVariation(vIndex, "price", parseFloat(e.target.value))}
                                        />

                                        <div>
                                        <h5>Sizes</h5>
                                        {variant.sizes.map((sz, sIndex) => (
                                            <div key={sIndex} style={{ display: "flex", gap: "10px", marginBottom: "0.5rem" }}>
                                            <label>Size</label>
                                            <input
                                                type="text"
                                                placeholder="Size"
                                                value={sz.size}
                                                onChange={(e) => updateSize(vIndex, sIndex, "size", e.target.value)}
                                            />
                                            <label>Quantity</label>
                                            <input
                                                type="number"
                                                placeholder="Qty"
                                                value={sz.quantity}
                                                onChange={(e) => updateSize(vIndex, sIndex, "quantity", parseInt(e.target.value))}
                                                required
                                            />
                                            <label>Price</label>
                                            <input
                                                type="number"
                                                placeholder="Price"
                                                value={sz.price}
                                                onChange={(e) => updateSize(vIndex, sIndex, "price", parseFloat(e.target.value))}
                                            />
                                            </div>
                                        ))}
                                        <button type="button" className='btn-add' onClick={() => addSizeToVariation(vIndex)}>+ Add Size</button>
                                        </div>
                                    </div>
                                )
                            })}
                            



                            <button type="submit">{editProductId ? 'Update Product' : 'Add Product'}</button>
                            <button onClick={() => setShowModal(false)} className='btn-cancel'>Cancel</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageProducts;
